import { useEffect, useRef, useState } from "react";
import { Download, ExternalLink, Loader2, Printer, X } from "lucide-react";
import { createDocumentPdfFile, openDocumentPdfTab } from "../../utils/documentPdf";

const safeName = (label, id) => `${String(label || "Document").replace(/[^A-Za-z0-9._-]+/g, "_")}_${id}.pdf`;

export default function DocumentPreviewDialog({ open, onClose, kind, id, label }) {
  const [file, setFile] = useState(null);
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  const frameRef = useRef(null);

  useEffect(() => {
    if (!open || !kind || !id) return undefined;
    const controller = new AbortController();
    let objectUrl = "";
    setFile(null);
    setUrl("");
    setError("");
    createDocumentPdfFile(kind, id, "Original for Recipient", safeName(label, id), controller.signal)
      .then((nextFile) => {
        if (controller.signal.aborted) return;
        setFile(nextFile);
        objectUrl = URL.createObjectURL(nextFile);
        setUrl(objectUrl);
      })
      .catch((err) => {
        if (!controller.signal.aborted) setError(err?.message || "Preview load nahi ho paaya");
      });
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, kind, id, label]);

  if (!open) return null;

  const download = () => {
    if (!file) return;
    const href = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.href = href;
    a.download = file.name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(href), 30000);
  };

  const print = () => {
    const viewer = frameRef.current?.contentWindow;
    if (viewer) {
      viewer.focus();
      viewer.print();
    }
  };

  const openFull = () => {
    openDocumentPdfTab(file);
  };

  return (
    <div className="fixed inset-0 z-[80] bg-slate-950/55 backdrop-blur-[1px] p-3 md:p-5" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="mx-auto flex h-full w-full max-w-[1240px] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-3">
          <div>
            <p className="text-base font-bold text-slate-900">{label || "Document Preview"}</p>
            <p className="text-xs text-slate-500">Exact official PDF preview — page se bahar jaane ki zarurat nahi.</p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={download} disabled={!file} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 disabled:opacity-40"><Download className="h-4 w-4" /> Download</button>
            <button type="button" onClick={print} disabled={!url} className="inline-flex items-center gap-1.5 rounded-lg bg-teal-700 px-3 py-2 text-sm font-semibold text-white disabled:opacity-40"><Printer className="h-4 w-4" /> Print</button>
            <button type="button" onClick={openFull} disabled={!file} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700"><ExternalLink className="h-4 w-4" /> New tab</button>
            <button type="button" onClick={onClose} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" aria-label="Close preview"><X className="h-5 w-5" /></button>
          </div>
        </div>
        <div className="min-h-0 flex-1 bg-slate-200 p-2 md:p-4">
          {!url && !error && <div className="flex h-full items-center justify-center text-slate-600"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Preparing official PDF preview...</div>}
          {error && <div role="alert" className="flex h-full items-center justify-center text-rose-700">{error}</div>}
          {url && <iframe ref={frameRef} title={label || "Document Preview"} src={`${url}#toolbar=0&navpanes=0&view=FitH`} className="h-full w-full rounded-xl border border-slate-300 bg-white shadow" />}
        </div>
      </div>
    </div>
  );
}
